import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Mail, RefreshCw, Send, CheckCircle2, AlertTriangle, XCircle, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_SUBJECT = "Teste KW Lead Growth League";
const DEFAULT_BODY = (
  "Este é um email de teste enviado a partir do painel de Configuração.\n\n" +
  "Se o estás a receber com sucesso, a configuração do domínio Resend e o envio " +
  "via lead@kwgrowthleague.com estão a funcionar."
);

function DomainStatusBadge({ status, error, canQuery }) {
  if (!canQuery) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-2 py-1 border border-[#3B82F6] text-[#3B82F6]" data-testid="email-domain-restricted">
        <ShieldCheck className="w-3 h-3" /> API restrita
      </span>
    );
  }
  if (error) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-2 py-1 border border-[#FF6B6B] text-[#FF6B6B]" data-testid="email-domain-error">
        <XCircle className="w-3 h-3" /> Erro
      </span>
    );
  }
  if (status === "verified") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-2 py-1 border border-[#22C55E] text-[#22C55E]" data-testid="email-domain-verified">
        <CheckCircle2 className="w-3 h-3" /> Verificado
      </span>
    );
  }
  if (status === "pending" || status === "not_started") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-2 py-1 border border-[#F59E0B] text-[#F59E0B]" data-testid="email-domain-pending">
        <AlertTriangle className="w-3 h-3" /> Pendente
      </span>
    );
  }
  if (status === "failed" || status === "temporary_failure") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-2 py-1 border border-[#FF6B6B] text-[#FF6B6B]" data-testid="email-domain-failed">
        <XCircle className="w-3 h-3" /> Falhou
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-2 py-1 border border-[#737373] text-[#737373]">
      —
    </span>
  );
}

export default function EmailConfigPanel() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState(user?.email || "");
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);

  const load = async () => {
    try {
      const r = await api.get("/admin/email/status");
      setStatus(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    (async () => { await load(); setLoading(false); })();
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
    toast.success("Estado atualizado");
  };

  const sendTest = async () => {
    if (!to.trim() || !to.includes("@")) {
      toast.error("Indica um destinatário válido");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/admin/email/test-custom", { to: to.trim(), subject: subject.trim() || undefined, body: body.trim() || undefined });
      toast.success(`Email enviado para ${r.data.to}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSending(false);
  };

  return (
    <div className="kw-card p-6 max-w-3xl" data-testid="email-config-panel">
      <div className="flex items-center gap-3 mb-5">
        <Mail className="w-6 h-6 text-[#E50000]" />
        <div className="flex-1">
          <div className="kw-overline">Comunicação</div>
          <h2 className="font-display text-2xl font-black uppercase">Configuração de Email</h2>
        </div>
        <button onClick={refresh} disabled={refreshing || loading} className="kw-btn-secondary inline-flex items-center gap-2 text-xs" data-testid="email-config-refresh">
          <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> Atualizar
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[#737373]">A carregar configuração…</p>
      ) : !status ? (
        <p className="text-sm text-[#FF6B6B]">Não foi possível carregar a configuração.</p>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-3 mb-5">
            <div className="border border-[#222] p-3" data-testid="email-config-from">
              <div className="kw-overline text-[10px]">Remetente (From)</div>
              <div className="font-mono text-xs text-[#E5E5E5] truncate" title={status.from}>{status.from || "—"}</div>
            </div>
            <div className="border border-[#222] p-3" data-testid="email-config-reply">
              <div className="kw-overline text-[10px]">Reply-To</div>
              <div className="font-mono text-xs text-[#E5E5E5] truncate" title={status.reply_to || "—"}>{status.reply_to || <span className="text-[#737373]">— (não configurado)</span>}</div>
            </div>
            <div className="border border-[#222] p-3 sm:col-span-2" data-testid="email-config-domain">
              <div className="flex items-center justify-between mb-1">
                <div className="kw-overline text-[10px]">Domínio</div>
                <DomainStatusBadge status={status.domain_status} error={status.error} canQuery={status.can_query_domains} />
              </div>
              <div className="font-mono text-xs text-[#E5E5E5]">{status.from_domain || "—"}</div>
              {!status.api_key_configured && (
                <p className="text-xs text-[#FF6B6B] mt-2">⚠️ RESEND_API_KEY não está configurada no backend.</p>
              )}
              {status.api_key_configured && !status.can_query_domains && (
                <p className="text-xs text-[#737373] mt-2 leading-relaxed">
                  A chave Resend atual tem permissão restrita a envio. Para verificar o estado do domínio via API,
                  cria uma chave com acesso &quot;Domains: Read&quot; em <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#E50000] hover:underline">resend.com/api-keys</a>.
                  Em alternativa, envia o email de teste abaixo — um envio bem-sucedido confirma que o domínio está verificado.
                </p>
              )}
            </div>
          </div>

          <div className="border-t border-[#222] pt-5">
            <div className="kw-overline mb-3">Enviar email de teste</div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#A3A3A3] mb-1">Destinatário</label>
                <input className="kw-input w-full" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="admin@kwlead.pt" data-testid="email-test-to" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#A3A3A3] mb-1">Assunto</label>
                <input className="kw-input w-full" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={DEFAULT_SUBJECT} data-testid="email-test-subject" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#A3A3A3] mb-1">Corpo</label>
                <textarea className="kw-input w-full min-h-[120px]" value={body} onChange={(e) => setBody(e.target.value)} placeholder={DEFAULT_BODY} data-testid="email-test-body" />
                <p className="text-[10px] text-[#737373] mt-1">Quebras de linha são preservadas; o conteúdo é enviado como HTML com escape.</p>
              </div>
              <button onClick={sendTest} disabled={sending || !status.api_key_configured} className="kw-btn-primary inline-flex items-center gap-2" data-testid="email-test-send">
                <Send className="w-4 h-4" /> {sending ? "A enviar…" : "Enviar email de teste"}
              </button>
            </div>
          </div>

          <p className="text-[10px] text-[#737373] mt-4 font-mono">
            Último check: {status.checked_at ? new Date(status.checked_at).toLocaleString("pt-PT") : "—"}
          </p>
        </>
      )}
    </div>
  );
}
